/*
  Linha do tempo de entrega ("Hoje → Amanhã → 15 Ago - 17 Ago").

  O cálculo é no cliente, lendo o relógio do visitante — mesma escolha da
  referência. Consequência a conhecer: quem estiver com a data do aparelho
  errada vê prazos errados, e o Liquid não tem como corrigir isso.

  Duas diferenças em relação à referência, ambas por causa dos dias úteis:

  1. Ela deixa "Tomorrow" escrito como texto fixo mesmo com a etapa
     configurada para 1-2 dias. Numa sexta-feira, com fim de semana pulado, o
     próximo dia útil é segunda e o texto mente. Aqui "Hoje" e "Amanhã" são
     produzidos pelo formatador, e só aparecem quando a data realmente é hoje
     ou amanhã.
  2. Contar dia útil é avançar UM DIA POR VEZ testando cada um, não somar N e
     corrigir depois: só assim um feriado no meio da contagem empurra o prazo.
*/
if (!customElements.get('delivery-timeline')) {
  customElements.define(
    'delivery-timeline',
    class DeliveryTimeline extends HTMLElement {
      connectedCallback() {
        this.months = (this.dataset.months || '').split(',').map((m) => m.trim());
        this.businessDays = this.dataset.businessDays === 'true';
        this.labelToday = this.dataset.today || '';
        this.labelTomorrow = this.dataset.tomorrow || '';

        this.holidays = new Set(
          (this.dataset.holidays || '')
            .split(',')
            .map((d) => d.trim())
            .filter(Boolean)
        );

        this.today = new Date();
        this.today.setHours(0, 0, 0, 0);

        this.render();
      }

      /* Aceita "25/12" (repete todo ano) e "2026-12-25" (data única). */
      isHoliday(date) {
        if (!this.holidays.size) return false;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return this.holidays.has(`${day}/${month}`) || this.holidays.has(`${date.getFullYear()}-${month}-${day}`);
      }

      isBusinessDay(date) {
        if (this.isHoliday(date)) return false;
        if (!this.businessDays) return true;
        const weekday = date.getDay();
        return weekday !== 0 && weekday !== 6;
      }

      /* Zero dias é sempre hoje, mesmo num sábado: o pedido foi feito hoje. */
      addDays(amount) {
        const date = new Date(this.today);
        let left = Math.max(0, amount);
        while (left > 0) {
          date.setDate(date.getDate() + 1);
          if (this.isBusinessDay(date)) left -= 1;
        }
        return date;
      }

      format(date) {
        const diff = Math.round((date - this.today) / 86400000);
        if (diff === 0 && this.labelToday) return this.labelToday;
        if (diff === 1 && this.labelTomorrow) return this.labelTomorrow;
        return `${date.getDate()} ${this.months[date.getMonth()] || ''}`.trim();
      }

      pad(number) {
        return String(number).padStart(2, '0');
      }

      monthName(date) {
        return this.months[date.getMonth()] || '';
      }

      /*
        [intervalo] — faixa compacta: com as duas pontas no mesmo mês o nome
        do mês aparece uma vez só ("18 a 20 Ago"); virando o mês (ou o ano),
        escreve os dois ("29 Ago a 01 Set"). Se uma das pontas for hoje ou
        amanhã, mantém a palavra em vez da data.
      */
      compactRange(start, end) {
        const a = this.format(start);
        const b = this.format(end);
        if (a === b) return a;
        if (a === this.labelToday || a === this.labelTomorrow || b === this.labelToday || b === this.labelTomorrow) {
          return `${a} a ${b}`;
        }
        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
          return `${this.pad(start.getDate())} a ${this.pad(end.getDate())} ${this.monthName(end)}`;
        }
        return `${this.pad(start.getDate())} ${this.monthName(start)} a ${this.pad(end.getDate())} ${this.monthName(end)}`;
      }

      render() {
        this.querySelectorAll('[data-timeline-date]').forEach((element) => {
          const template = element.dataset.template || '';
          const min = parseInt(element.dataset.min, 10) || 0;
          const max = parseInt(element.dataset.max, 10) || 0;

          const startDate = this.addDays(min);
          const endDate = this.addDays(Math.max(min, max));
          const start = this.format(startDate);
          const end = this.format(endDate);
          // Quando as duas pontas caem no mesmo dia — ou porque min e max são
          // iguais, ou porque um feriado juntou as duas — mostra uma data só.
          const range = start === end ? start : `${start} - ${end}`;

          element.textContent = template
            .replaceAll('[intervalo]', this.compactRange(startDate, endDate))
            .replaceAll('[prazo]', range)
            .replaceAll('[inicio]', start)
            .replaceAll('[fim]', end);
        });
      }
    }
  );
}
